import React from "react";
import Highcharts from "highcharts";

import Column from "components/visuals/Column";
import useColourPalette from "components/visuals/useColourPalette";

import { useAppSelector } from "store";
import { selectUserInfo } from "modules/auth/authSlice";
import stringUtils from "utils/stringUtils";
import numberFormatter from "utils/numberFormatter";
import { selectRangeOverviewFilteredProducts } from "modules/customer/tools/product/productSlice";
import { Box, useTheme } from "@material-ui/core";

const SalesByProduct = () => {
    const theme = useTheme();
    const colourPalette = useColourPalette();
    const clientName = useAppSelector(selectUserInfo).companyDisplayName;
    const products = useAppSelector(selectRangeOverviewFilteredProducts);
    const { isLoading, hasErrors, data } = products;

    const categories = products.data.map(product => product.name);

    const optimisedSales: Highcharts.SeriesOptionsType = {
        name: "Optimised sales",
        data: data.map(product => ({
            y: product.sales.optimisedSales,
            category: product.sales.name,
        })),
        color: colourPalette.categorical[0],
        type: "column",
    };

    const estimatedSales: Highcharts.SeriesOptionsType = {
        name: "Estimated sales",
        data: data.map(product => ({
            y: product.sales.estimatedSales,
            category: product.sales.name,
        })),
        color: colourPalette.categorical[1],
        type: "column",
    };

    const clientSourcedSales: Highcharts.SeriesOptionsType = {
        name: `${clientName} sourced sales`,
        data: data.map(product => ({
            y: product.sales.clientSourcedSales,
            category: product.sales.name,
        })),
        color: colourPalette.categorical[2],
        type: "column",
    };

    const series = [optimisedSales, estimatedSales, clientSourcedSales];

    const options: Highcharts.Options = {
        chart: {
            // height: 600,
            type: "column",
            scrollablePlotArea: {
                minWidth: 100 * products.data.length
            },
            spacingRight: 0
        },
        xAxis: {
            type: 'category',
            categories,
            labels: {
                enabled: true
            },
            scrollbar: {
                enabled: true
            }
        },
        yAxis: {
            title: {
                text: "Sales"
            },
            labels: {
                // @ts-ignore
                formatter: function () { return numberFormatter.toGBP(this.value, 0); },
                enabled: true
            },
            gridLineWidth: 1
        },
        plotOptions: {
            series: {
                dataLabels: {
                    enabled: false
                }
            }
        },
        legend: {
            enabled: true
        },
        series,
        tooltip: {
            enabled: true,
            shared: true,
            useHTML: true,
            formatter: function () {
                if (!this.points) {
                    return "";
                }
                const points = this.points ?? [];
                const values = points.map(point => numberFormatter.toGBP(point.y, 1));
                const captions = points.map(point => point.series.name);
                const categoryFormattingArr = points.map((point, index) => {
                    return `color:${String(point.color)};font-weight:bold`;
                });
                categoryFormattingArr.push(`color:${theme.palette.text.primary};font-weight:bold`);
                const relevantHeadroomValue = products.data.find(product => product.name === points[0].x)?.sales.salesHeadroom ?? 0;
                values.push(numberFormatter.toGBP(relevantHeadroomValue, 1));
                captions.push("Headroom");
                return `<table>${stringUtils.tooltipHTML(captions, {
                    header: `${points.at(0)?.key ?? ""}`,
                    values,
                    categoryFormattingArr
                })}</table>`;
            },
        }
    };

    return (
        <Box data-cy="historic-prod-growth-store-vs-comp">
            <Column loading={isLoading} error={hasErrors} options={options} />
        </Box>
    );
};

export default SalesByProduct;
